import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { GetShowcaseCategories, GetShowcases, GetShowcaseItems } from "../../../api/queries";
import { ShowcaseCategory, Showcase, ShowcaseItem } from "../../../api/zod-types";
import CategoryCard from "../CategoryCard";
import { useLocation } from "react-router-dom";
import { useEffect } from "react";


function slugify(str: string) {
    return str.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export default function MainCategoryGridShowcase() {
  

    const location = useLocation();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);
    
    const navigate = useNavigate();

    const queryClient = useQueryClient();

function handlePrefetch() {
    queryClient.prefetchQuery({
        queryKey: ["showcasecategories"],
        queryFn: GetShowcaseCategories
    });
    queryClient.prefetchQuery({
        queryKey: ["showcases"],
        queryFn: GetShowcases
    });
    queryClient.prefetchQuery({
        queryKey: ["showcaseitems"],
        queryFn: GetShowcaseItems
    });
}

    // Fetch categories, showcases, and items
    const { data: catData, isLoading, isError } = useQuery({
        queryKey: ["showcasecategories"],
        queryFn: GetShowcaseCategories,
        staleTime: 0,
    });
    const { data: showcasesData } = useQuery({
        queryKey: ["showcases"],
        queryFn: GetShowcases,
        staleTime: 0,
    });
    const { data: showcaseItemsData } = useQuery({
        queryKey: ["showcaseitems"],
        queryFn: GetShowcaseItems,
        staleTime: 0,
    });

    // Parse categories
    // let categories: ShowcaseCategory[] = [];
    // if (catData?.data) {
    //     let parsed = typeof catData.data === "string" ? JSON.parse(catData.data) : catData.data;
    //     categories = (parsed?.results || []).filter((cat: ShowcaseCategory) => cat.categorylevel === "0");
    // }
    // Parse all categories (level 0 and level 1)
    let allCategories: ShowcaseCategory[] = [];
    if (catData?.data) {
        let parsed = typeof catData.data === "string" ? JSON.parse(catData.data) : catData.data;
        allCategories = parsed?.results || [];
    }

    // Only display level 0 categories
    const level0Categories = allCategories.filter(cat => cat.categorylevel === "0");

    // Parse showcases and items
    let showcases: Showcase[] = [];
    let showcaseItems: ShowcaseItem[] = [];
    if (showcasesData) {
        showcases = Array.isArray(showcasesData?.data) ? showcasesData.data : (showcasesData?.data?.results || []);
    }
    if (showcaseItemsData) {
        showcaseItems = Array.isArray(showcaseItemsData?.data) ? showcaseItemsData.data : (showcaseItemsData?.data?.results || []);
    }

    // Count items for each level 0 category
    function getProductCountForLevel0Category(categoryName: string): number {
        // Find all level 1 categories under this level 0
        const level1Cats = allCategories.filter(cat => cat.categorylevel === "1" && cat.categorytag === categoryName);
        const level1Names = level1Cats.map(cat => cat.categoryname);
        // Find all showcases under these level 1 categories
        const showcasesForCat = showcases.filter(s => level1Names.includes(s.showcasecategoryname));
        const showcaseNames = showcasesForCat.map(s => s.showcasename);
        // Count all showcase items linked to those showcases
        return showcaseItems.filter(item => showcaseNames.includes(item.showcasename)).length;
    }
    //only count unique products for each level 0 category
    function getUniqueProductCountForLevel0Category(categoryName: string): number {
    // Find all level 1 categories under this level 0
    const level1Cats = allCategories.filter(cat => cat.categorylevel === "1" && cat.categorytag === categoryName);
    const level1Names = level1Cats.map(cat => cat.categoryname);
    // Find all showcases under these level 1 categories
    const showcasesForCat = showcases.filter(s => level1Names.includes(s.showcasecategoryname));
    const showcaseNames = showcasesForCat.map(s => s.showcasename);
    // Deduplicate by productid
    const uniqueProducts = showcaseItems
        .filter(item => showcaseNames.includes(item.showcasename))
        .reduce((acc, curr) => {
            acc[curr.productid] = true;
            return acc;
        }, {} as Record<string, boolean>);
    return Object.keys(uniqueProducts).length;
}

    if (isLoading) {
        return <div className="text-center py-12 text-lg text-gray-500">Loading...</div>;
    }
    if (isError) {
        return <div className="text-center py-12 text-lg text-red-500">Failed to load categories.</div>;
    }
    

    return (
        <div className="flex flex-col w-full bg-gradient-to-b from-[#f8fafc] to-[#f2f2f2] pb-16 min-h-screen  gap-8 ">            
            {/* <h2 className="text-4xl font-extrabold text-center text-gray-900 mt-12 mb-8 tracking-tight animate-fade-in-up">
                Explore Our Product Catalogue
            </h2> */}
            {/* Hero Section - full width, background image, overlay */}
            <section
                className="relative w-full h-[340px] md:h-[420px] flex items-center justify-center mb-8"
                style={{
                    backgroundImage: "url('https://hncnzwww.blob.core.windows.net/www/img/NewWebPhotos/SmbPage/hnc_web_smb_hero.jpg')",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            >

            
                {/* Overlay */}
                <div className="absolute inset-0 bg-black/80 backdrop-blur-sm"></div>
                {/* Decorative elements (optional) */}
                <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-red-400 rounded-full animate-ping opacity-60"></div>
                <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-white rounded-full animate-pulse opacity-80"></div>
                {/* Main hero content */}
                <div className="relative z-10 flex flex-col items-center justify-center w-full h-full text-center px-4">
                    <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold py-4 text-white animate-fade-in-up delay-200">
                        Explore Our Product Catalogue
                    </h1>
                    <p className="text-lg md:text-xl text-white/90 py-4 animate-fade-in-up delay-400 max-w-2xl mx-auto">
                        Discover our full range of categories and products. Click a category to browse available items.
                    </p>
                </div>
            </section>
            <div className="max-w-6xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 px-2 md:px-4 lg:px-8 xl:px-12 text-black">
                    {level0Categories.length === 0 ? (
                        <div className="col-span-full text-center text-gray-500">No categories found.</div>
                    ) : (
                        level0Categories
                        .filter(cat => getProductCountForLevel0Category(cat.categoryname) > 0)
                        .map((cat, idx) => (
                            <CategoryCard
                                key={cat.categoryid}
                                title={cat.categoryname}
                                image={cat.categoryimageurl}
                                url={`/products/${slugify(cat.categoryname)}`}
                                onClick={() => navigate(`/products/${slugify(cat.categoryname)}`)}
                                onMouseEnter={handlePrefetch}
                                productCount={getProductCountForLevel0Category(cat.categoryname)}
                                // productCount={getUniqueProductCountForLevel0Category(cat.categoryname)}
                                animateClassName="animate-fade-in-up"
                                animationDelay={`${idx * 100}ms`}
                            />
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}